/**
 * Typewriter Animation for Search Input Placeholder
 * Cycles through search terms with a natural typing effect
 */

class TypewriterAnimation {
  constructor(element, options = {}) {
    this.element = element;
    this.words = options.words || [
      'Search',
      'Casual wear',
      'Formal wear', 
      'Ethnic wear',
      'Kurta & Jacket',
      'Lounge wear'
    ];
    this.typeSpeed = options.typeSpeed || 100; // ms per character
    this.deleteSpeed = options.deleteSpeed || 50; // ms per character
    this.pauseTime = options.pauseTime || 2000; // ms to pause between words
    this.caretBlinkSpeed = options.caretBlinkSpeed || 500; // ms for caret blink
    
    this.currentWordIndex = 0;
    this.currentCharIndex = 0;
    this.isDeleting = false;
    this.isTyping = false;
    this.caretVisible = true;
    this.searchTyped = false; // Track if "Search" has been typed in current cycle
    
    this.init();
  }
  
  init() {
    if (!this.element) return;
    
    // Start the animation
    this.type();
    
    // Start caret blinking
    this.startCaretBlink();
  }
  
  type() {
    if (this.isTyping) return;
    this.isTyping = true;
    
    const currentWord = this.words[this.currentWordIndex];
    
    // Check if this is "Search" and if it should be typed
    if (currentWord === 'Search' && this.searchTyped) {
      // "Search" is already typed, skip to next word
      this.currentWordIndex = (this.currentWordIndex + 1) % this.words.length;
      this.currentCharIndex = 0;
      setTimeout(() => {
        this.isTyping = false;
        this.type();
      }, this.pauseTime);
      return;
    }
    
    if (this.isDeleting) {
      // Delete character
      this.currentCharIndex--;
      
      // If this is "Search", don't delete it - keep it static
      if (currentWord === 'Search') {
        this.element.placeholder = 'Search|';
        this.isDeleting = false;
        this.searchTyped = true;
        this.currentWordIndex = (this.currentWordIndex + 1) % this.words.length;
        this.currentCharIndex = 0;
        setTimeout(() => {
          this.isTyping = false;
          this.type();
        }, this.pauseTime);
        return;
      } else {
        // For other words, delete normally but keep "Search" prefix
        const currentText = currentWord.substring(0, this.currentCharIndex);
        this.element.placeholder = this.searchTyped ? 'Search ' + currentText + '|' : currentText + '|';
      }
      
      if (this.currentCharIndex === 0) {
        this.isDeleting = false;
        this.currentWordIndex = (this.currentWordIndex + 1) % this.words.length;
        this.currentCharIndex = 0;
        
        // If we're back to "Search" and it was already typed, reset the cycle
        if (this.currentWordIndex === 0 && this.searchTyped) {
          this.searchTyped = false;
        }
        
        setTimeout(() => {
          this.isTyping = false;
          this.type();
        }, this.pauseTime);
        return;
      }
      
      setTimeout(() => {
        this.isTyping = false;
        this.type();
      }, this.deleteSpeed);
    } else {
      // Type character
      this.currentCharIndex++;
      
      // If this is "Search", type it normally
      if (currentWord === 'Search') {
        this.element.placeholder = currentWord.substring(0, this.currentCharIndex) + '|';
      } else {
        // For other words, type them with "Search" prefix
        const currentText = currentWord.substring(0, this.currentCharIndex);
        this.element.placeholder = this.searchTyped ? 'Search ' + currentText + '|' : currentText + '|';
      }
      
      if (this.currentCharIndex === currentWord.length) {
        // Word complete, pause then start deleting
        setTimeout(() => {
          this.isDeleting = true;
          this.isTyping = false;
          this.type();
        }, this.pauseTime);
        return;
      }
      
      setTimeout(() => {
        this.isTyping = false;
        this.type();
      }, this.typeSpeed);
    }
  }
  
  startCaretBlink() {
    setInterval(() => {
      if (!this.isTyping && !this.isDeleting) {
        this.caretVisible = !this.caretVisible;
        const currentWord = this.words[this.currentWordIndex];
        
        // If current word is "Search" and it's already typed, keep it static
        if (currentWord === 'Search' && this.searchTyped) {
          this.element.placeholder = this.caretVisible ? 'Search|' : 'Search';
        } else {
          const currentText = currentWord.substring(0, this.currentCharIndex);
          // Keep "Search" prefix for other words when it's been typed
          if (this.searchTyped) {
            this.element.placeholder = this.caretVisible ? 'Search ' + currentText + '|' : 'Search ' + currentText;
          } else {
            this.element.placeholder = this.caretVisible ? currentText + '|' : currentText;
          }
        }
      }
    }, this.caretBlinkSpeed);
  }
  
  // Public method to update words
  updateWords(newWords) {
    this.words = newWords;
    this.currentWordIndex = 0;
    this.currentCharIndex = 0;
    this.isDeleting = false;
    this.isTyping = false;
  }
  
  // Public method to stop animation
  stop() {
    this.isTyping = false;
    this.isDeleting = false;
  }
  
  // Public method to restart animation
  restart() {
    this.currentWordIndex = 0;
    this.currentCharIndex = 0;
    this.isDeleting = false;
    this.isTyping = false;
    this.searchTyped = false;
    this.type();
  }
}

// Initialize typewriter animation when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  const searchInput = document.getElementById('header-search');
  const mobileSearchInput = document.getElementById('wizzy-search-input');
  
  // Create typewriter animation for desktop header search
  if (searchInput) {
    const typewriter = new TypewriterAnimation(searchInput, {
      typeSpeed: 100,    // 100ms per character when typing
      deleteSpeed: 50,   // 50ms per character when deleting
      pauseTime: 800,    // 800ms pause between words (reduced from 2000ms)
      caretBlinkSpeed: 500 // 500ms for caret blink
    });
    
    // Make it globally accessible for debugging if needed
    window.headerTypewriter = typewriter;
  }
  
  // Create typewriter animation for mobile search
  if (mobileSearchInput) {
    const mobileTypewriter = new TypewriterAnimation(mobileSearchInput, {
      typeSpeed: 100,    // 100ms per character when typing
      deleteSpeed: 50,   // 50ms per character when deleting
      pauseTime: 800,    // 800ms pause between words (reduced from 2000ms)
      caretBlinkSpeed: 500 // 500ms for caret blink
    });
    
    // Make it globally accessible for debugging if needed
    window.mobileTypewriter = mobileTypewriter;
  }
});

// Export for module usage if needed
if (typeof module !== 'undefined' && module.exports) {
  module.exports = TypewriterAnimation;
} 